import { Button, Message, Uploader, useToaster } from 'rsuite';
import ImageIcon from '@rsuite/icons/Image';
import React, { useEffect } from 'react';
import { FileType } from 'rsuite/esm/Uploader/Uploader';
import { uploadFormData } from '../api/request';
import { FormModal } from './FormModal';
import { useCsrfHeaders } from './useCsrfHeaders';

type Props = {
  title: string;
  multiple?: boolean;
  fieldName?: string;
  actionUrl: string;
  object: null | {
    id?: string;
    image?: string;
  };
  onUploaded?: (payload: Record<string, unknown>) => void;
  handleModalClose: () => void;
};

export default function UploadImageModal({
  title,
  actionUrl,
  multiple = false,
  fieldName = 'image',
  object,
  onUploaded,
  handleModalClose,
}: Props) {
  const [fileList, setFileList] = React.useState<FileType[]>([
    {
      url: object?.image,
    },
  ]);
  const csrfHeaders = useCsrfHeaders();

  useEffect(() => {
    if (object === null) {
      setFileList([]);
      return;
    }
    setFileList(object.image ? [{ url: object.image }] : []);
  }, [object]);
  const toaster = useToaster();

  const upload = async () => {
    if (!fileList.length || !actionUrl) {
      return;
    }
    if (!fileList[0].blobFile) {
      return;
    }

    // send multipart/form-data request
    const formData = new FormData();
    if (multiple) {
      fileList.forEach(file => {
        formData.append(fieldName, file.blobFile as Blob);
      });
    } else {
      formData.append(fieldName, fileList[0].blobFile as Blob);
    }
    const response = await uploadFormData(actionUrl, formData);
    if (response.ok) {
      let payload: Record<string, unknown> | null = null;
      try {
        payload = (await response.json()) as Record<string, unknown>;
      } catch (err) {
        payload = null;
      }
      if (payload && onUploaded) {
        onUploaded(payload);
      }
      toaster.push(
        <Message showIcon type={'success'} closable>
          Зображення успішно змінено.
        </Message>,
        { placement: 'topEnd', duration: 1500 }
      );
      handleModalClose();
    } else {
      toaster.push(
        <Message showIcon type={'error'} closable>
          Не вдалося завантажити зображення.
        </Message>,
        { placement: 'topEnd', duration: 1500 }
      );
    }
  };

  return (
    <FormModal
      open={!!object}
      onClose={handleModalClose}
      title={title}
      footer={
        <>
          <Button onClick={upload} appearance="primary">
            Завантажити
          </Button>
          <Button onClick={handleModalClose} appearance="subtle">
            Скасувати
          </Button>
        </>
      }
    >
      <Uploader
        id={'image-uploader'}
        multiple={multiple}
        draggable
        listType="picture"
        action={actionUrl}
        name={fieldName}
        autoUpload={false}
        headers={csrfHeaders}
        fileList={fileList.filter(file => !!file.url || !!file.blobFile)}
        onError={(status, file, event, xhr) => {
          let message = 'Помилка завантаження файлу';
          if (xhr.responseText) {
            try {
              const body = JSON.parse(xhr.responseText);
              message = body?.message?.message || body?.message || message;
            } catch {
              // backend returned non-JSON (e.g. nginx HTML error page)
            }
          }
          toaster.push(
            <Message type="error" showIcon closable>
              {message}
            </Message>,
            { placement: 'topEnd', duration: 1500 }
          );
        }}
        onChange={fileList => {
          if (fileList.length > 1) {
            setFileList(fileList.splice(1, 1));
          } else {
            setFileList(fileList);
          }
        }}
      >
        <button>
          <ImageIcon />
        </button>
      </Uploader>
    </FormModal>
  );
}
